import React from "react";
import { Box, Typography } from "@mui/material";
import { cardSx, cardTitleSx, INK } from "./chartTheme";

/**
 * One card shell for every panel on the analytics page.
 *
 * The header strip is a fixed height with its own rule, so titles sit on the
 * same baseline and every chart body starts at the same y across the row —
 * that alignment is what makes the grid read as one dashboard.
 */
export function ChartCard({ title, subtitle, action, children, bodySx = {} }) {
  return (
    <Box
      sx={{
        ...cardSx,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          px: 2.5,
          py: 2,
          minHeight: 64,
          borderBottom: `1px solid ${INK.grid}`,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={cardTitleSx}>{title}</Typography>
          {subtitle ? (
            <Typography sx={{ fontSize: "12px !important", color: INK.muted, mt: 0.25 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {action ? <Box sx={{ flex: "0 0 auto" }}>{action}</Box> : null}
      </Box>

      <Box
        sx={{
          flex: "1 1 auto",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          px: 2.5,
          py: 2.25,
          ...bodySx,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

/**
 * A headline number with its label. `dotColor` carries the entity it belongs to
 * (the same hue that status wears in the donut), so the tiles and the chart
 * below them read as the same data.
 */
export function StatTile({ label, value, hint, dotColor }) {
  return (
    <Box
      sx={{
        ...cardSx,
        height: "100%",
        px: 2.25,
        py: 1.75,
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.875, minWidth: 0 }}>
        {dotColor ? (
          <Box sx={{ width: 8, height: 8, borderRadius: "999px", flex: "0 0 auto", backgroundColor: dotColor }} />
        ) : null}
        <Typography
          sx={{
            fontSize: "11.5px !important",
            fontWeight: "600 !important",
            color: INK.muted,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: "26px !important", fontWeight: "700 !important", color: INK.primary, lineHeight: 1.15, letterSpacing: "-0.02em" }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: "11.5px !important", color: INK.muted, minHeight: 17 }}>
        {hint}
      </Typography>
    </Box>
  );
}

/** Small state pill — sprint status, never colour alone (it always prints its word). */
export function StatusPill({ label, tone = "neutral" }) {
  const tones = {
    active: { color: "#0f7a53", background: "#e6f6ef" },
    completed: { color: "#3d4a8f", background: "#eceffb" },
    neutral: { color: INK.secondary, background: "#f1f3f8" },
  };
  const style = tones[tone] || tones.neutral;

  return (
    <Typography
      component="span"
      sx={{
        fontSize: "11px !important",
        fontWeight: "700 !important",
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        borderRadius: "999px",
        px: 1,
        py: "2px",
        color: style.color,
        backgroundColor: style.background,
        flex: "0 0 auto",
      }}
    >
      {label}
    </Typography>
  );
}
